import { initializeCarousels } from '~/javascripts/carousel.js'
import { prefetchDiscoverImages } from './discover-image-prefetch.mjs';
function scrollToMap(event) {
  event.preventDefault();
  const mapElement = document.getElementById('mapView');
  if (mapElement) {
    mapElement.scrollIntoView({ behavior: 'smooth' });
  } else {
    console.error('Map element not found');
  }
}

function initializeDiscoverPage() {
  discoverPaginationState.manualLoadCount = 0;
  discoverPaginationState.isLoading = false;
  teardownLoadMoreObserver();
  initializeHeroIntroToggle();

  // Limit carousel initialization to discover content so we don't touch unrelated sliders
  // (e.g., leaderboard/custom collections not shown on this page).
  const discoverScope = document.getElementById('all-activities') || document;
  initializeCarousels(discoverScope);

  initializeBizLazyLoad();
  initializeMapLazyLoad();
  initializeCityInsightsLazyLoad();
  initializeResourcesLazyLoad();
  initializeFiltersLazyLoad();
  initializeBizContainers();
  initializeDiscoverPagination();
  initializeDiscoverTooltips();
  initializeViewToggle();
  // Hearts + share bar live in shortlist-hearts.js; re-hydrate on (re)init.
  if (typeof window.syncDiscoverShortlistHearts === 'function') {
    window.syncDiscoverShortlistHearts();
  }
  restoreDiscoverState();
}

// ---- Grid/List view toggle -------------------------------------------------
// List is the default. The chosen mode is applied as a class on the stable
// `.discover-results-area` wrapper (not on #event-results, which AJAX replaces)
// and persisted in localStorage so it survives navigation, filtering, and
// infinite-scroll appends.
const VIEW_MODE_STORAGE_KEY = 'discoverViewMode';

function getStoredViewMode() {
  try {
    return window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  } catch (e) {
    return null;
  }
}

function storeViewMode(mode) {
  try {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  } catch (e) {
    /* storage unavailable (private mode) — mode still applies for this session */
  }
}

function applyViewMode(mode) {
  const isList = mode !== 'grid'; // default to list for anything but an explicit grid
  document.querySelectorAll('.discover-results-area').forEach((area) => {
    area.classList.toggle('discover-view--list', isList);
    area.classList.toggle('discover-view--grid', !isList);
  });
  // The results/sort row sits above the list container (not inside it), so mirror the
  // view mode onto it — list view offsets the count to align with the first card column.
  document.querySelectorAll('.filters-meta-row--discover').forEach((row) => {
    row.classList.toggle('filters-meta-row--list', isList);
  });
  document.querySelectorAll('[data-view-toggle] [data-view-mode]').forEach((btn) => {
    const pressed = (btn.dataset.viewMode === 'grid') === !isList;
    btn.classList.toggle('view-toggle__btn--active', pressed);
    btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
  });
}

function initializeViewToggle() {
  const toggles = document.querySelectorAll('[data-view-toggle]');
  if (!toggles.length) return;

  applyViewMode(getStoredViewMode() === 'grid' ? 'grid' : 'list');

  toggles.forEach((toggle) => {
    if (toggle.dataset.viewToggleBound === 'true') return;
    toggle.dataset.viewToggleBound = 'true';
    toggle.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-view-mode]');
      if (!btn) return;
      event.preventDefault();
      const mode = btn.dataset.viewMode === 'grid' ? 'grid' : 'list';
      storeViewMode(mode);
      applyViewMode(mode);
    });
  });
}

function initializeDiscoverTooltips() {
  // Ensure Bootstrap is available
  if (!window.bootstrap || !window.bootstrap.Tooltip) {
    return;
  }

  const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
  tooltipTriggerList.forEach(function (tooltipTriggerEl) {
    // Dispose any existing tooltip to prevent duplicates
    const existingInstance = bootstrap.Tooltip.getInstance(tooltipTriggerEl);
    if (existingInstance) {
      existingInstance.dispose();
    }

    // Create new tooltip instance
    const tooltip = new bootstrap.Tooltip(tooltipTriggerEl, {
      html: true,
      trigger: 'hover focus'
    });
  });
}
function initializeLazyFrame(selector, callback) {
  const root = document.querySelector(selector);
  if (!root || root.dataset.loaded === 'true') return;

  const load = () => {
    if (root.dataset.loaded === 'true') return;
    callback(root);
  };

  if (typeof IntersectionObserver !== 'undefined') {
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          obs.disconnect();
          load();
        }
      });
    }, { rootMargin: '200px 0px' });
    observer.observe(root);
  } else {
    load();
  }
}

function fetchInto(element, url, onSuccess = () => {}) {
  fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to load ${url}: ${response.status}`);
      }
      return response.text();
    })
    .then((html) => {
      element.innerHTML = html;
      element.dataset.loaded = 'true';
      onSuccess(element);
    })
    .catch((error) => {
      console.warn(`Failed to load ${url}`, error);
    });
}

function initializeMapLazyLoad() {
  initializeLazyFrame('[data-map-url]', (root) => {
    // Lazy load map-specific CSS
    if (!document.querySelector('link[href*="discover_map"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/assets/discover_map.css';
      document.head.appendChild(link);
    }
    
    fetchInto(root, root.dataset.mapUrl, () => {
      initializeCarousels(root);
    });
  });
}

function initializeCityInsightsLazyLoad() {
  initializeLazyFrame('[data-city-insights-url]', (root) => {
    fetchInto(root, root.dataset.cityInsightsUrl, () => {
      initializeCarousels(root);
    });
  });
}

function initializeResourcesLazyLoad() {
  initializeLazyFrame('[data-resources-url]', (root) => {
    fetchInto(root, root.dataset.resourcesUrl, () => {
      initializeCarousels(root);
    });
  });
}

function initializeFiltersLazyLoad() {
  const root = document.querySelector('[data-filters-url]');
  if (!root) {
    document.dispatchEvent(new CustomEvent('filters:rendered'));
    return;
  }
  if (root.dataset.loaded === 'true') {
    document.dispatchEvent(new CustomEvent('filters:rendered'));
    return;
  }

  fetchInto(root, root.dataset.filtersUrl, () => {
    initializeCarousels(root);
    document.dispatchEvent(new CustomEvent('filters:rendered'));
  });
}

function initializeBizContainers() {
  document.querySelectorAll('[data-biz-container]').forEach((container) => {
    if (container.dataset.bizInitialized === 'true') return;

    container.dataset.bizInitialized = 'true';
    const list = container.querySelector('[data-biz-list]');
    if (!list) return;

    const initialCount = parseInt(list.dataset.initialCount, 10) || 15;
    const totalCountAttr = parseInt(list.dataset.totalCount || '', 10);
    const itemsCount = Number.isFinite(totalCountAttr) ? totalCountAttr : list.childElementCount;
    const viewAllButton = container.querySelector('[data-biz-view-all]');

    if (viewAllButton && initialCount >= itemsCount) {
      viewAllButton.classList.add('hidden');
    }
  });
}

const discoverPaginationState = {
  manualLoadCount: 0,
  isLoading: false,
  observer: null,
  handlersBound: false,
  prefetchObserver: null,
  prefetchAbortController: null,
  prefetchedPage: null,
  prefetchedPayload: null,
  prefetchedButton: null,
  prefetchedImages: [],
  isPrefetching: false
};

function getLoadMoreButton() {
  return document.querySelector('[data-load-more-button]');
}

function teardownLoadMoreObserver() {
  if (discoverPaginationState.observer) {
    discoverPaginationState.observer.disconnect();
    discoverPaginationState.observer = null;
  }
}

function teardownPrefetchObserver() {
  if (discoverPaginationState.prefetchObserver) {
    discoverPaginationState.prefetchObserver.disconnect();
    discoverPaginationState.prefetchObserver = null;
  }
}

function clearPrefetchState({ abort = false } = {}) {
  if (abort && discoverPaginationState.prefetchAbortController) {
    discoverPaginationState.prefetchAbortController.abort();
  }

  discoverPaginationState.prefetchAbortController = null;
  discoverPaginationState.prefetchedPage = null;
  discoverPaginationState.prefetchedPayload = null;
  discoverPaginationState.prefetchedButton = null;
  discoverPaginationState.prefetchedImages = [];
  discoverPaginationState.isPrefetching = false;
}

function maybeAttachAutoLoad(button) {
  if (!button || typeof IntersectionObserver === 'undefined') return;

  const threshold = parseInt(button.dataset.manualThreshold || '2', 10);
  if (Number.isNaN(threshold) || discoverPaginationState.manualLoadCount < threshold) {
    return;
  }

  teardownLoadMoreObserver();

  discoverPaginationState.observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !discoverPaginationState.isLoading) {
        triggerLoadMore(entry.target, { autoTriggered: true });
      }
    });
  }, { rootMargin: '200px 0px' });

  discoverPaginationState.observer.observe(button);
}

function buildLoadMoreUrl(button, targetPage) {
  const baseUrl = button.dataset.baseUrl || window.location.pathname;
  const queryString = button.dataset.queryString || '';
  const searchParams = new URLSearchParams(queryString);

  searchParams.set('page', targetPage);
  // REMOVED: searchParams.set('format', 'js');
  // We're using .json extension in the URL instead (added below in triggerLoadMore)

  const paramsString = searchParams.toString();
  return paramsString ? `${baseUrl}?${paramsString}` : baseUrl;
}

function setButtonLoadingState(button, isLoading) {
  if (!button) return;

  if (isLoading) {
    if (!button.dataset.originalText) {
      button.dataset.originalText = button.textContent.trim();
    }
    const loadingText = button.dataset.loadingText || 'Loading…';
    button.innerHTML = `<span class="ds-btn-spinner" aria-hidden="true"></span> ${loadingText}`;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  } else {
    const originalText = button.dataset.originalText;
    if (originalText) {
      button.textContent = originalText;
    }
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

function appendEventResults(html) {
  if (!html) return null;
  const eventsContainer = document.getElementById('event-results');
  if (!eventsContainer) return null;

  const firstNewIndex = eventsContainer.children.length;
  eventsContainer.insertAdjacentHTML('beforeend', html);

  // Stagger the new cards in; the cap keeps a long page from feeling slow.
  Array.prototype.slice.call(eventsContainer.children, firstNewIndex).forEach((card, i) => {
    card.classList.add('ds-anim-fade-up');
    card.style.animationDelay = `${Math.min(i * 40, 320)}ms`;
    card.addEventListener('animationend', () => {
      card.classList.remove('ds-anim-fade-up');
      card.style.animationDelay = '';
    }, { once: true });
  });

  return eventsContainer;
}

function updatePaginationContainer(html) {
  const paginationContainer = document.querySelector('[data-pagination-container]');
  if (!paginationContainer) return null;

  teardownLoadMoreObserver();
  paginationContainer.innerHTML = html;
  teardownPrefetchObserver();
  clearPrefetchState({ abort: true });
  return paginationContainer.querySelector('[data-load-more-button]');
}

function updateMapWithPayload(data, { autoTriggered, page }) {
  // Skip map updates on subsequent pages to avoid large payload work
  if (page && page > 1) return;
  if (typeof updateMap === 'function' && data && Array.isArray(data.events_data)) {
    updateMap(data.events_data);
  }
}

function processPaginationPayload(payload, { autoTriggered, page }) {
  if (!payload) return;

  const eventsContainer = appendEventResults(payload.html);
  if (eventsContainer) {
    initializeCarousels(eventsContainer);
    if (typeof window.observeNewEventCards === 'function') {
      window.observeNewEventCards(eventsContainer);
    }
    if (typeof window.updateDiscoverPriceEstimates === 'function') {
      window.updateDiscoverPriceEstimates();
    }
    if (typeof window.syncDiscoverShortlistHearts === 'function') {
      window.syncDiscoverShortlistHearts();
    }
  }

  if (!autoTriggered) {
    discoverPaginationState.manualLoadCount += 1;
  }

  if (payload.pagination) {
    updatePaginationContainer(payload.pagination);
  }

  // Only update map for page 1 to reduce repeated heavy updates
  const pageNum = typeof payload.page === 'number' ? payload.page : undefined;
  updateMapWithPayload(payload, { autoTriggered, page: pageNum });
}

async function prefetchNextPage(button) {
  if (!button ||
      discoverPaginationState.isLoading ||
      discoverPaginationState.isPrefetching) {
    return;
  }

  const nextPage = parseInt(button.dataset.nextPage || '0', 10);
  if (!nextPage) return;

  if (discoverPaginationState.prefetchedButton === button &&
      discoverPaginationState.prefetchedPage === nextPage &&
      discoverPaginationState.prefetchedPayload) {
    return;
  }

  discoverPaginationState.isPrefetching = true;
  const controller = new AbortController();
  discoverPaginationState.prefetchAbortController = controller;

  try {
    const requestUrl = buildLoadMoreUrl(button, nextPage);
    // CRITICAL: Append .json for explicit JSON format (prevents Cloudflare cache collision)
    const jsonUrl = requestUrl.includes('?') 
      ? requestUrl.replace('?', '.json?')
      : requestUrl + '.json';
    const response = await fetch(jsonUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      signal: controller.signal
    });

    if (!response.ok) {
      throw new Error(`Prefetch failed with status ${response.status}`);
    }

    const payload = await response.json();
    // Filters can replace the button while this request is in flight.
    if (controller.signal.aborted || getLoadMoreButton() !== button) return;
    discoverPaginationState.prefetchedPayload = payload;
    discoverPaginationState.prefetchedPage = nextPage;
    discoverPaginationState.prefetchedButton = button;
    discoverPaginationState.prefetchedImages = prefetchDiscoverImages(payload.html);
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.warn('Discover pagination prefetch failed:', error);
    }
    if (discoverPaginationState.prefetchAbortController === controller) clearPrefetchState();
  } finally {
    if (discoverPaginationState.prefetchAbortController === controller) {
      discoverPaginationState.isPrefetching = false;
    }
  }
}

function setupPrefetchObserver(button) {
  if (!button || typeof IntersectionObserver === 'undefined') return;

  teardownPrefetchObserver();

  discoverPaginationState.prefetchObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        prefetchNextPage(entry.target);
      }
    });
  }, { rootMargin: '600px 0px' });

  discoverPaginationState.prefetchObserver.observe(button);
}

async function triggerLoadMore(button, { autoTriggered = false } = {}) {
  if (!button || discoverPaginationState.isLoading) return;

  const nextPage = parseInt(button.dataset.nextPage || '0', 10);
  if (!nextPage) return;

  discoverPaginationState.isLoading = true;
  setButtonLoadingState(button, true);

  let loadSucceeded = false;
  let payload = null;

  try {
    if (discoverPaginationState.prefetchedButton === button &&
        discoverPaginationState.prefetchedPage === nextPage &&
        discoverPaginationState.prefetchedPayload) {
      payload = discoverPaginationState.prefetchedPayload;
      clearPrefetchState();
    } else {
      clearPrefetchState({ abort: true });
      const requestUrl = buildLoadMoreUrl(button, nextPage);
      // CRITICAL: Append .json for explicit JSON format (prevents Cloudflare cache collision)
      const jsonUrl = requestUrl.includes('?') 
        ? requestUrl.replace('?', '.json?')
        : requestUrl + '.json';
      const response = await fetch(jsonUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`Pagination request failed with status ${response.status}`);
      }

      payload = await response.json();
    }

    processPaginationPayload(payload, { autoTriggered, page: nextPage });
    loadSucceeded = true;
  } catch (error) {
    console.error('Failed to load more activities:', error);
  } finally {
    discoverPaginationState.isLoading = false;
    if (document.body.contains(button)) {
      setButtonLoadingState(button, false);
    }

    if (loadSucceeded) {
      initializeDiscoverPagination();
    }
  }
}

function bindLoadMoreHandler() {
  if (discoverPaginationState.handlersBound) return;

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-load-more-button]');
    if (!button) return;

    event.preventDefault();
    triggerLoadMore(button);
  });

  discoverPaginationState.handlersBound = true;
}

function initializeDiscoverPagination() {
  bindLoadMoreHandler();

  const button = getLoadMoreButton();
  if (!button) {
    teardownLoadMoreObserver();
    teardownPrefetchObserver();
    clearPrefetchState({ abort: true });
    return;
  }

  maybeAttachAutoLoad(button);
  setupPrefetchObserver(button);
}

document.addEventListener('discover:pagination-replaced', () => {
  teardownLoadMoreObserver();
  teardownPrefetchObserver();
  clearPrefetchState({ abort: true });
  discoverPaginationState.manualLoadCount = 0;
  initializeDiscoverPagination();
});

// ---- Back-to-results state restoration -------------------------------------
// When a visitor opens an event card (same tab) and comes back, restore how
// many pages were loaded and the scroll position. State is keyed by the full
// results URL so different filter combinations don't collide.
const DISCOVER_STATE_TTL_MS = 30 * 60 * 1000;

function discoverStateKey() {
  return `discoverState:${window.location.pathname}${window.location.search}`;
}

function saveDiscoverState() {
  try {
    const btn = getLoadMoreButton();
    const nextPage = btn ? parseInt(btn.dataset.nextPage || '2', 10) : 2;
    sessionStorage.setItem(discoverStateKey(), JSON.stringify({
      scrollY: window.scrollY,
      loadedThroughPage: Math.max(1, (nextPage || 2) - 1),
      savedAt: Date.now()
    }));
  } catch (e) {
    // sessionStorage unavailable/full — restoration is best-effort only.
  }
}

async function restoreDiscoverState() {
  let state = null;
  try {
    const raw = sessionStorage.getItem(discoverStateKey());
    if (!raw) return;
    sessionStorage.removeItem(discoverStateKey());
    state = JSON.parse(raw);
  } catch (e) {
    return;
  }
  if (!state || Date.now() - (state.savedAt || 0) > DISCOVER_STATE_TTL_MS) return;

  // Only restore on back/forward navigation; a fresh visit via a nav link
  // should start at the top.
  const navEntry = performance.getEntriesByType?.('navigation')?.[0];
  if (navEntry && navEntry.type !== 'back_forward') return;

  let guard = 0;
  while (guard < 30) {
    guard += 1;
    const btn = getLoadMoreButton();
    if (!btn) break;
    const nextPage = parseInt(btn.dataset.nextPage || '0', 10);
    if (!nextPage || nextPage > state.loadedThroughPage) break;
    // eslint-disable-next-line no-await-in-loop
    await triggerLoadMore(btn, { autoTriggered: true });
  }

  if (state.scrollY > 0) {
    requestAnimationFrame(() => window.scrollTo({ top: state.scrollY }));
  }
}

function initializeHeroIntroToggle() {
  const heroContainers = document.querySelectorAll('[data-hero-intro]');
  if (!heroContainers.length) return;

  heroContainers.forEach((container) => {
    if (container.dataset.heroIntroInitialized === 'true') return;

    const toggle = container.querySelector('[data-hero-intro-toggle]');
    const content = container.querySelector('[data-hero-intro-target]');
    const text = container.querySelector('[data-hero-intro-toggle-text]');

    if (!toggle || !content) {
      container.dataset.heroIntroInitialized = 'true';
      return;
    }

    const collapsedLabel = toggle.dataset.collapsedLabel || 'Show more city intro';
    const expandedLabel = toggle.dataset.expandedLabel || 'Show less city intro';
    const collapsedText = toggle.dataset.collapsedText || 'Show more';
    const expandedText = toggle.dataset.expandedText || 'Show less';

    if (text) {
      text.textContent = collapsedText;
    }

    toggle.addEventListener('click', () => {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      const nextExpanded = !isExpanded;

      toggle.setAttribute('aria-expanded', String(nextExpanded));
      toggle.setAttribute('aria-label', nextExpanded ? expandedLabel : collapsedLabel);
      if (text) {
        text.textContent = nextExpanded ? expandedText : collapsedText;
      }
      content.classList.toggle('ds-hero-intro__copy--collapsed', !nextExpanded);
    });

    container.dataset.heroIntroInitialized = 'true';
  });
}


function initializeBizLazyLoad() {
  const root = document.querySelector('[data-biz-url]');
  if (!root || root.dataset.bizLoaded === 'true') return;

  const loadBizList = () => {
    if (root.dataset.bizLoaded === 'true') return;

    fetch(root.dataset.bizUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load business list: ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        root.innerHTML = html;
        root.dataset.bizLoaded = 'true';
        initializeCarousels(root);
        initializeBizContainers();
      })
      .catch((error) => {
        console.warn('Failed to load local listings', error);
      });
  };

  if (typeof IntersectionObserver !== 'undefined') {
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          obs.disconnect();
          loadBizList();
        }
      });
    }, { rootMargin: '200px 0px' });
    observer.observe(root);
  } else {
    loadBizList();
  }
}

// Initialize on both DOMContentLoaded and Turbo load
document.addEventListener('DOMContentLoaded', () => {
  initializeDiscoverPage();
});

document.addEventListener('turbo:load', () => {
  initializeDiscoverPage();
});

// Use event delegation for better performance
document.addEventListener('click', (event) => {
  // Leaving the results for an event page (same tab): remember where we were.
  if (event.target.closest('#event-results a[href]')) {
    saveDiscoverState();
  }

  // Handle favorite toggle
  if (event.target.matches('.ev-heart input')) {
    const value = event.target.value;
    if (window.TeamShortlist) {
      if (event.target.checked) window.TeamShortlist.add(value);
      else window.TeamShortlist.remove(value);
      return;
    }
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    if (!csrfToken) {
      event.preventDefault();
      const loginTrigger = document.querySelector('[data-login-modal-trigger]');
      if (loginTrigger) {
        loginTrigger.click();
      } else {
        console.warn('Login required to favorite events.');
      }
      return;
    }
    
    fetch(`/toggle_favorite/${value}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': csrfToken,
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).catch(error => {
      console.warn('Failed to toggle favorite:', error);
    });
    return;
  }
  
  // Handle map view link
  if (event.target.closest('.map-view-link')) {
    scrollToMap(event);
    return;
  }
  
  // Handle local biz view all button
  const viewAllButton = event.target.closest('[data-biz-view-all]');
  if (viewAllButton) {
    event.preventDefault();
    const container = viewAllButton.closest('[data-biz-container]');
    if (!container) return;

    const list = container.querySelector('[data-biz-list]');
    if (!list) return;

    list.querySelectorAll('[data-biz-item]').forEach(item => {
      item.classList.remove('hidden');
    });

    viewAllButton.classList.add('hidden');
  }
});
